import React, { useState, useEffect, useCallback } from "react";
import "./locationUsers.css";
import MessageImg from "../../../picture/Message.png";
import ActiveImg from "./picture/check.png";
import { useGlobalContext } from "../../../Context/Context";
import CloseIcon from "../../picture/cancel.png";
import SearchIcon from "./picture/Search.png";
import PlusIcon from "./picture/Plus.png";
import Button from "../../Custom/Button/Button"

const LocationUsers = (
  location,
  selectedCompany,
  sideBarRef,
  location_input,
  isLandScapeProp,
) => {
  const [modalOpened, setModalOpened] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [addUserModalOpened, setAddUserModalOpened] = useState(false);
  const [confirmDeleteModalOpened, setConfirmDeleteModalOpened] = useState(false);
  const [userToAdd, setUserToAdd] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteFromGroup, setDeleteFromGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { selectedMainEntity } = useGlobalContext();

  // API Configuration
  const API_BASE_URL = "https://data.nadzor.app/api/admin_panel/user_company";
  const ENDPOINTS = {
    reporters: `${API_BASE_URL}/getReporters.php`,
    moderators: `${API_BASE_URL}/getModerators.php`,
    visitors: `${API_BASE_URL}/getVisitors.php`,
    allUsers: `${API_BASE_URL}/getUnsorted.php`,
    addPrivilege: `${API_BASE_URL}/addUserPrivilege.php`,
    removePrivilege: `${API_BASE_URL}/removeUserPrivilege.php`,
    userLocations: `${API_BASE_URL}/getUserLocations.php`
  };



  // Replace these with actual values from your context or props
  const LOCATION_ID = location.location_input; // Replace with actual location_id
  const COMPANY_ID = selectedMainEntity.company_id;  // Replace with actual company_id

  // User data states
  const [moderators, setModerators] = useState([]);
  const [reporters, setReporters] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [others, setOthers] = useState([]);

  // Search states
  const [reporterSearch, setReporterSearch] = useState("");
  const [visitorSearch, setVisitorSearch] = useState("");
  const [othersSearch, setOthersSearch] = useState("");

  // Active search states (what's actually being filtered)
  const [activeReporterSearch, setActiveReporterSearch] = useState("");
  const [activeVisitorSearch, setActiveVisitorSearch] = useState("");
  const [activeOthersSearch, setActiveOthersSearch] = useState("");

  // User location info
  const [userLocations, setUserLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);


  const fetchUserLocations = async (userId) => {
    setLoadingLocations(true);
    try {
      const response = await fetch(ENDPOINTS.userLocations, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId
        })
      });

      const result = await response.json();

      if (result.status === 200) {
        setUserLocations(result.data);
      } else {
        setUserLocations([]);
      }
    } catch (error) {
      console.error('Error fetching user locations:', error);
      setUserLocations([]);
    } finally {
      setLoadingLocations(false);
    }
  };

  // API call function for users with privileges
  const fetchUsers = async (endpoint, userType) => {
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          location_id: LOCATION_ID,
          company_id: COMPANY_ID
        })
      });

      const result = await response.json();

      if (result.status === 200) {
        return result.data.map(user => ({
          user_id: user.user_id,
          user_email: user.user_email,
          active: user.active,
          status_flag: user.status_flag,
          is_currently_reporter: user.is_currently_reporter || 0,
          is_currently_visitor: user.is_currently_visitor || 0
        }));
      } else {
        console.warn(`No ${userType} found:`, result.message);
        return [];
      }
    } catch (error) {
      console.error(`Error fetching ${userType}:`, error);
      throw error;
    }
  };

  // API call function for all users (for "Others" section)
  const fetchAllUsers = async () => {
    try {
      const response = await fetch(ENDPOINTS.allUsers, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company_id: COMPANY_ID
        })
      });

      const result = await response.json();

      if (result.status === 200) {
        return result.data.map(user => ({
          user_id: user.user_id,
          user_email: user.user_email,
          archived: user.archived
        }));
      } else {
        console.warn('No users found:', result.message);
        return [];
      }
    } catch (error) {
      console.error('Error fetching all users:', error);
      throw error;
    }
  };

  // Reload all data function
  const reloadAllData = async () => {
    try {
      const [reportersData, moderatorsData, visitorsData, allUsersData] = await Promise.all([
        fetchUsers(ENDPOINTS.reporters, 'reporters'),
        fetchUsers(ENDPOINTS.moderators, 'moderators'),
        fetchUsers(ENDPOINTS.visitors, 'visitors'),
        fetchAllUsers()
      ]);

      setReporters(reportersData);
      setModerators(moderatorsData);
      setVisitors(visitorsData);

      // Filter "others" - exclude ONLY currently active users, include former users so they can be re-added
      const currentlyActiveUserIds = new Set([
        ...reportersData.filter(u => u.status_flag === 'currently_active').map(u => u.user_id),
        ...moderatorsData.map(u => u.user_id), // moderators are always current
        ...visitorsData.filter(u => u.status_flag === 'currently_active').map(u => u.user_id)
      ]);

      const othersData = allUsersData.filter(user =>
        !currentlyActiveUserIds.has(user.user_id) && !user.archived
      );

      setOthers(othersData);
    } catch (err) {
      setError('Failed to reload user data');
      console.error('Error reloading users:', err);
    }
  };

  // Add privilege function
  // const addUserPrivilege = async (userId, privilege) => {
  //   try {
  //     const response = await fetch(ENDPOINTS.addPrivilege, {
  //       method: 'POST',
  //       headers: {
  //         'Content-Type': 'application/json',
  //       },
  //       body: JSON.stringify({
  //         user_id: userId,
  //         location_id: LOCATION_ID,
  //         privilege: privilege
  //       })
  //     });

  //     const result = await response.json();

  //     if (result.status === 200) {
  //       return true;
  //     } else {
  //       console.error('Failed to add privilege:', result.message);
  //       return false;
  //     }
  //   } catch (error) {
  //     console.error('Error adding privilege:', error);
  //     return false;
  //   }
  // };

  // Add privilege function with settings creation
const addUserPrivilege = async (userId, privilege) => {
  try {
    // First, add the privilege
    const privilegeResponse = await fetch(ENDPOINTS.addPrivilege, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: userId,
        location_id: LOCATION_ID,
        privilege: privilege
      })
    });

    const privilegeResult = await privilegeResponse.json();

    if (privilegeResult.status === 200) {
      // Then create default settings for this user-location combo
      const settingsResponse = await fetch('https://data.nadzor.app/api/user_location_settings/create.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          location_id: LOCATION_ID,
          pdf_active: 1,
          archive_active: 1,
          comment_item: 1,
          img_number: '1',
          document_num: '1'
        })
      });

      const settingsResult = await settingsResponse.json();
      
      if (settingsResult.status === 200) {
        console.log('Settings created successfully for user');
      } else {
        console.warn('Failed to create settings:', settingsResult.message);
      }

      return true;
    } else {
      console.error('Failed to add privilege:', privilegeResult.message);
      return false;
    }
  } catch (error) {
    console.error('Error adding privilege:', error);
    return false;
  }
};
  // Remove privilege function
  const removeUserPrivilege = async (userId) => {
    try {
      const response = await fetch(ENDPOINTS.removePrivilege, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          location_id: LOCATION_ID
        })
      });

      const result = await response.json();

      if (result.status === 200) {
        return true;
      } else {
        console.error('Failed to remove privilege:', result.message);
        return false;
      }
    } catch (error) {
      console.error('Error removing privilege:', error);
      return false;
    }
  };

  // Load all user data on component mount
  useEffect(() => {
    const loadAllUsers = async () => {
      setLoading(true);
      setError(null);

      try {
        await reloadAllData();
      } catch (err) {
        setError('Failed to load user data');
        console.error('Error loading users:', err);
      } finally {
        setLoading(false);
      }
    };

    loadAllUsers();
  }, []);

  // Function to handle user click
  const handleUserClick = async (userObj) => {
    setSelectedUser(userObj);
    setModalOpened(true);
    await fetchUserLocations(userObj.user_id);
  };

  const closeModal = () => {
    setModalOpened(false);
    setSelectedUser(null);
  };

  // Function to handle plus button click
  const handleAddUser = (userObj) => {
    setUserToAdd(userObj);
    setAddUserModalOpened(true);
  };

  const closeAddUserModal = () => {
    setAddUserModalOpened(false);
    setUserToAdd(null);
  };

  // Function to handle delete confirmation
  const handleDeleteUser = (userObj, group) => {
    setUserToDelete(userObj);
    setDeleteFromGroup(group);
    setConfirmDeleteModalOpened(true);
  };

  const closeConfirmDeleteModal = () => {
    setConfirmDeleteModalOpened(false);
    setUserToDelete(null);
    setDeleteFromGroup(null);
  };

  // Search functions
  const handleReporterSearch = () => {
    setActiveReporterSearch(reporterSearch);
  };

  const handleVisitorSearch = () => {
    setActiveVisitorSearch(visitorSearch);
  };

  const handleOthersSearch = () => {
    setActiveOthersSearch(othersSearch);
  };

  // Remove functions with API calls
  const removeModerator = async (userToRemove) => {
    const success = await removeUserPrivilege(userToRemove.user_id);
    if (success) {
      await reloadAllData();
    } else {
      setError('Failed to remove moderator privilege');
    }
  };

  const removeReporter = async (userToRemove) => {
    const success = await removeUserPrivilege(userToRemove.user_id);
    if (success) {
      await reloadAllData();
    } else {
      setError('Failed to remove reporter privilege');
    }
  };

  const removeVisitor = async (userToRemove) => {
    const success = await removeUserPrivilege(userToRemove.user_id);
    if (success) {
      await reloadAllData();
    } else {
      setError('Failed to remove visitor privilege');
    }
  };

  // Confirm delete function
  const confirmDelete = () => {
    if (deleteFromGroup === 'moderators') {
      removeModerator(userToDelete);
    } else if (deleteFromGroup === 'reporters') {
      removeReporter(userToDelete);
    } else if (deleteFromGroup === 'visitors') {
      removeVisitor(userToDelete);
    }
    closeConfirmDeleteModal();
  };

  // Add user functions with API calls
  const addUserToReporters = async () => {
    if (userToAdd) {
      const success = await addUserPrivilege(userToAdd.user_id, 3);
      if (success) {
        await reloadAllData();
      } else {
        setError('Failed to add reporter privilege');
      }
    }
    closeAddUserModal();
  };

  const addUserToModerators = async () => {
    if (userToAdd) {
      const success = await addUserPrivilege(userToAdd.user_id, 2);
      if (success) {
        await reloadAllData();
      } else {
        setError('Failed to add moderator privilege');
      }
    }
    closeAddUserModal();
  };

  const addUserToVisitors = async () => {
    if (userToAdd) {
      const success = await addUserPrivilege(userToAdd.user_id, 4);
      if (success) {
        await reloadAllData();
      } else {
        setError('Failed to add visitor privilege');
      }
    }
    closeAddUserModal();
  };

  // Filter functions using active search states
  const filteredReporters = reporters.filter(user =>
    user.user_email.toLowerCase().includes(activeReporterSearch.toLowerCase())
  );

  const filteredVisitors = visitors.filter(user =>
    user.user_email.toLowerCase().includes(activeVisitorSearch.toLowerCase())
  );

  const filteredOthers = others.filter(user =>
    user.user_email.toLowerCase().includes(activeOthersSearch.toLowerCase())
  );

  // Loading state
  if (loading) {
    return (
      <div className="location-users-wrapper">
        <div style={{ textAlign: 'center', padding: '20px' }}>
          Učitavanje korisnika...
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="location-users-wrapper">
        <div style={{ textAlign: 'center', padding: '20px', color: 'red' }}>
          Error: {error}
          <button onClick={() => window.location.reload()} style={{ marginLeft: '10px' }}>
            Reload
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="location-users-wrapper">
        <div className="luw-header">
          <div className="luw-header-mods">
            <Button activation={true}>MODERATORI</Button>
          </div>
          {moderators.map((user, index) => (
            <div key={`mod-${index}-${user.user_id}`}>
              <div onClick={() => handleUserClick(user)} style={{ cursor: 'pointer' }}>
                {user.user_email}
              </div>
              <div className="remove-mod" onClick={() => handleDeleteUser(user, 'moderators')} style={{ cursor: 'pointer' }}>
                <img src={CloseIcon} alt="" />
              </div>
            </div>
          ))}
        </div>

        <hr />

        <div className="luw-body">
          <div className="reporter-list">
            <div className="luw-list-header">
              <Button activation={true}>REPORTERI</Button>
            </div>
            {reporters.length > 5 && (
              <div className="luw-search">
                <input
                  type="text"
                  value={reporterSearch}
                  onChange={(e) => setReporterSearch(e.target.value)}
                  placeholder="Pretraži reportere..."
                  onKeyPress={(e) => e.key === 'Enter' && handleReporterSearch()}
                />
                <div onClick={handleReporterSearch}>
                  <Button activation={true}>
                    <img src={SearchIcon} alt="" />
                  </Button>
                </div>
              </div>
            )}
            <div className="luw-list-body">
              {filteredReporters.map((user, index) => (
                <div
                  key={`rep-${index}-${user.user_id}`}
                  className="luw-list-item"
                  style={{
                    backgroundColor: user.status_flag === 'currently_active' ? '#d4edda' : 'transparent'
                  }}
                >
                  <div
                    onClick={() => handleUserClick(user)}
                    style={{
                      cursor: 'pointer',
                      color: user.status_flag === 'archived' ? '#999' :
                        user.status_flag === 'was_active' ? '#28a745' : '#000'
                    }}
                  >
                    {user.user_email}
                  </div>
                  {user.status_flag === 'currently_active' && (
                    <img src={CloseIcon} alt="" onClick={() => handleDeleteUser(user, 'reporters')} style={{ cursor: 'pointer' }} />
                  )}
                  {user.status_flag === 'was_active' && (
                    <img src={PlusIcon} alt="" onClick={() => handleAddUser(user)} style={{ cursor: 'pointer' }} />
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="reporter-list">
            <div className="luw-list-header">
              <Button activation={true}>POSETIOCI</Button>
            </div>
            {visitors.length > 5 && (
              <div className="luw-search">
                <input
                  type="text"
                  value={visitorSearch}
                  onChange={(e) => setVisitorSearch(e.target.value)}
                  placeholder="Pretraži posetioce..."
                  onKeyPress={(e) => e.key === 'Enter' && handleVisitorSearch()}
                />
                <div onClick={handleVisitorSearch}>
                  <Button activation={true}>
                    <img src={SearchIcon} alt="" />
                  </Button>
                </div>
              </div>
            )}
            <div className="luw-list-body">
              {filteredVisitors.map((user, index) => (
                <div
                  key={`vis-${index}-${user.user_id}`}
                  className="luw-list-item"
                  style={{
                    backgroundColor: user.status_flag === 'currently_active' ? '#d4edda' : 'transparent'
                  }}
                >
                  <div
                    onClick={() => handleUserClick(user)}
                    style={{
                      cursor: 'pointer',
                      color: user.status_flag === 'archived' ? '#999' :
                        user.status_flag === 'was_active' ? '#28a745' : '#000'
                    }}
                  >
                    {user.user_email}
                  </div>
                  {user.status_flag === 'currently_active' && (
                    <img src={CloseIcon} alt="" onClick={() => handleDeleteUser(user, 'visitors')} style={{ cursor: 'pointer' }} />
                  )}
                  {user.status_flag === 'was_active' && (
                    <img src={PlusIcon} alt="" onClick={() => handleAddUser(user)} style={{ cursor: 'pointer' }} />
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="reporter-list">
            <div className="luw-list-header">
              <Button activation={true}>OSTALI</Button>
            </div>
            <div className="luw-search">
              <input
                type="text"
                value={othersSearch}
                onChange={(e) => setOthersSearch(e.target.value)}
                placeholder="Pretraži ostale..."
                onKeyPress={(e) => e.key === 'Enter' && handleOthersSearch()}
              />
              <div onClick={handleOthersSearch}>
                <Button activation={true}>
                  <img src={SearchIcon} alt="" />
                </Button>
              </div>
            </div>
            <div className="luw-list-body">
              {filteredOthers.map((user, index) => (
                <div key={`oth-${index}-${user.user_id}`} className="luw-list-item">
                  <div onClick={() => handleUserClick(user)} style={{ cursor: 'pointer' }}>
                    {user.user_email}
                    {user.archived && <span style={{ color: '#999', marginLeft: '8px' }}>(Archived)</span>}
                  </div>
                  <img src={PlusIcon} alt="" onClick={() => handleAddUser(user)} style={{ cursor: 'pointer' }} />
                </div>
              ))}
            </div>
          </div>

          <div className="reporter-list">
            {modalOpened &&
              <div className="luw-modal">
                <div className="modal-header">
                  <button className="modal-close" onClick={closeModal}>
                    <img src={CloseIcon} alt="Close" />
                  </button>
                </div>
                  <div style={{ margin: '15px' }}>
                    <strong>Lokacije sa privilegijama:</strong>
                    {loadingLocations ? (
                      <p>Loading locations...</p>
                    ) : userLocations.length > 0 ? (
                      <ul style={{ marginTop: '10px', paddingLeft: '20px', overflow: "auto", height: "180px" }}>
                        {userLocations.map((location, index) => (
                          <li key={index} style={{ marginBottom: '5px' }}>
                            <strong>{location.location_name}</strong> - {location.privilege_name}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p>Nema privilegija na lokacijama.</p>
                    )}
                  </div>
              </div>
            }
          </div>
        </div>
      </div>

      {/* Centered Add User Modal */}
      {addUserModalOpened && (
        <div className="modal-overlay" onClick={closeAddUserModal} style={{justifyContent: "center"}}>
          <div className="centered-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Dodaj korisnika</h3>
              <button className="modal-close" onClick={closeAddUserModal}>
                ✕
              </button>
            </div>
            <div className="modal-content1">
              <p><strong>Korisnik:</strong> {userToAdd?.user_email}</p>
              <p>Izaberite grupu:</p>
              <div className="role-buttons">
                <button onClick={addUserToReporters} className="role-button">
                  Reporteri
                </button>
                <button onClick={addUserToModerators} className="role-button">
                  Moderatori
                </button>
                <button onClick={addUserToVisitors} className="role-button">
                  Posetioci
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Centered Confirm Delete Modal */}
      {confirmDeleteModalOpened && (
        <div className="modal-overlay" onClick={closeConfirmDeleteModal}>
          <div className="centered-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Potvrdi brisanje</h3>
              <button className="modal-close" onClick={closeConfirmDeleteModal}>
                ✕
              </button>
            </div>
            <div className="modal-content">
              <p>Da li ste sigurni da želite da uklonite korisnika?</p>
              <p><strong>{userToDelete?.user_email}</strong></p>
              <div className="confirm-buttons">
                <button onClick={confirmDelete} className="confirm-button confirm-yes">
                  Da
                </button>
                <button onClick={closeConfirmDeleteModal} className="confirm-button confirm-no">
                  Ne
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LocationUsers;